/**
 !Pay attention! only the RedirectPopUp component is responsible for activate the service
 **/

import {storageService} from './storageService';
import siaStore from '../stores/siaStore';
import settingsStore from '../stores/settingsStore';
import {request} from './HttpRequestService';
import logger from './AiLogger';
import loadService from './loadService';

interface IStateMachine{
    pageName?:string,
    component?: string,
    basicProps?:any,
    storeOrServiceVariables?:any
}

class ClientStateMachineService {
    private sessionId = '';
    private storeOrServiceVariables = {};
    private startRecord = false;
    private siaStore = siaStore;
    private settingsStore = settingsStore;


    setActiveServicesToDefault = (serviceList) => this.storeOrServiceVariables = serviceList;

    getSessionId = () => this.sessionId;

    /**
     * setStartRecord - set the service to start record
     */
    setStartRecord = () => this.startRecord = true;

    /**
     * loadStateMachine - load last state by session id
     */
    loadStateMachine = async () => {
        try {
            const res = await request.get(storageService.loadFromStorage('token'), 'SESSION_STORAGE', false, this.sessionId);

            if (res.data.Session_storage) {
                return JSON.parse(res.data.Session_storage || '{}');
            }

            return {};
        } catch (e) {
            await logger.sendLog('Error in load state', e, 'error');

            return {};
        }

    } ;

    createNewSession = async () => {
        try {
            const res = await request.post(storageService.loadFromStorage('token'), 'SESSION', {'session_task': 'create'});

            if (res.data.No_credits) {
                return false;
            }
            this.sessionId = res.data.session_id;

            return true;
        } catch (e) {
            await logger.sendLog('error in creating new session', e, 'error');
        }

    }

    /**
     * isSessionExist - find if session exist
     */
    isSessionExist = async () => {

        try {
            const res = await request.get(storageService.loadFromStorage('token'), 'SESSION', false, '');

            if (res.data.Active) {
                this.sessionId = res.data.Sessions[0];
                try {
                    const oldState = await this.loadStateMachine();

                    return {
                        isSession: true,
                        oldState,
                        isCredit: true
                    };

                } catch (e) {
                    await logger.sendLog('error in get session_storage active', e, 'error');

                    return {
                        isSession: false,
                        oldState: {},
                        isCredit: true
                    };
                }
            }
            const isCreditLeft = await this.createNewSession();

            return {
                isSession: false,
                oldState: {},
                isCredit: isCreditLeft
            };
        } catch (e) {
            await logger.sendLog('error in get session_id active', e, 'error');

            return {
                isSession: false,
                oldState: {},
                isCredit: true
            };
        }

    }

    disableLstSession =async () => {
        storageService.removeFromStorage('lastUrl');
        this.setActiveServicesToDefault({siaStore: {}, userStore: {}, settingsStore: {}, loadService: {}});
        this.setStartRecord();
        await this.removeStateMachine();
        await this.createNewSession();
        await logger.sendLog(`user ${storageService.loadFromStorage('username')} ended session`);
    }

    /**
     * removeStateMachine - delete session id record
     */
    removeStateMachine = async () => {
        try {
            await request.post(storageService.loadFromStorage('token'), 'SESSION', {'session_task': 'cancel', 'session_id': this.sessionId});

        } catch (e) {
            await logger.sendLog('error in get session_storage load', e, 'error');
        }
    };

    /**
     * saveStateMachine - set the new state
     */
    saveStateMachine = async (newSate:IStateMachine) => {
        if (this.startRecord) {
            const oldSate = await this.loadStateMachine();

            const state = {
                ...oldSate,
                ...newSate
            };

            try {
                if (this.startRecord && this.sessionId) {
                    await request.post(
                        storageService.loadFromStorage('token'),
                        'SESSION_STORAGE',
                        {'session_id': this.sessionId,
                            'session_data': JSON.stringify(state)});
                }
            } catch (e) {
                await logger.sendLog('error in save session_storage load', e, 'error');
            }
        }

    }

    /**
     * setStoreOrServiceData - set the data of the store or service
     * @param services - store or service name and data list
     * @param extraData - if need other parts of state machine
     */
    setStoreOrServiceData = async (services:{serviceName:string, newData:any}[], extraData = {}) => {
        if (this.startRecord) {
            services.forEach(({serviceName, newData}) => {
                const serviceParams = this.storeOrServiceVariables[serviceName];

                if (serviceParams) {
                    Object.keys(newData).forEach((key) => serviceParams[key] = newData[key]);
                }
            });
            const newState = Object.keys(extraData).length > 0 ? {storeOrServiceVariables: this.storeOrServiceVariables, ...extraData} : {storeOrServiceVariables: this.storeOrServiceVariables};

            await this.saveStateMachine(newState);

        }
    }

    /**
     * renderLastSession - render the last state
     * @param history - from react-router-dom - will redirect to to the last component
     */
    renderLastSession =async (history) => {
        const oldSate = await this.loadStateMachine();

        stateMachine.setActiveServicesToDefault(oldSate.storeOrServiceVariables);
        const servicesList = oldSate.storeOrServiceVariables;

        servicesList ? Object.keys(servicesList).forEach((serviceName) => this.setServiceDataForUi(serviceName, servicesList)) : this.setActiveServicesToDefault({siaStore: {}, userStore: {}, settingsStore: {}, loadService: {}});
        this.sendUiToRoute(history, oldSate);
    }

    /**
     * setServiceDataForUi - this method will set the service in render process
     * @param serviceName - service name
     * @param servicesList - list of the services
     */
    setServiceDataForUi = (serviceName, servicesList) => {
        const serviceInOldState = servicesList[serviceName];
        const serviceInActiveState = serviceName === 'loadService' ? loadService : this[serviceName];

        serviceInOldState && serviceInActiveState &&
        Object.keys(serviceInOldState).forEach((key) => {
            this.isNeedToActiveFunctionFromService(key)
                ? this.activateFunctionInService(serviceInOldState, serviceInActiveState, key)
                : serviceInActiveState[key] = serviceInOldState[key];
        });
    }

    /**
     * isNeedToActiveFunctionFromService - check in render process if need to activate a function from service
     * @param key - key of the last service
     */
    isNeedToActiveFunctionFromService = (key) => key === 'func';

    /**
     * activateFunctionInService - if need to active a function in service, this method will activate it
     * @param serviceInOldState - service from the storage
     * @param serviceInActiveState - service in this script
     * @param key - key of the last service
     */
    activateFunctionInService = (serviceInOldState, serviceInActiveState, key) => {
        const funcArguments = serviceInOldState.funcArg.map((arg) => (arg === 'siaStore' ? this.siaStore : arg));
        const funcName = serviceInOldState[key];

        serviceInActiveState[funcName](...funcArguments);
    }

    /**
     * sendUiToRoute - this will re route to the right component
     * @param history - history object (from react-router)
     * @param oldSate - the oldState from storage
     */
    sendUiToRoute = (history, oldSate) => {
        history.push({
            pathname: `/${oldSate.component}`,
            state: oldSate.basicProps
        });
    }

    isStateEmpty = (state) => {
        for (const key in state) {
            if (Object.keys(state[key]).length > 0) {
                return false;
            }
        }

        return true;
    }
}

const stateMachine = new ClientStateMachineService();

export default stateMachine;
